import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, Inbox, CheckCircle2, XCircle, Clock, MapPin, Lock, Ruler, Calendar } from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

const SECTION_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const SECTION_COLOR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#D4A24B' };

export default function LeadOffersInbox() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const isLocked = !!(sectionParam && SECTION_LABEL[sectionParam]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [declineId, setDeclineId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/api/lead-offers?mine=true`, { credentials: 'include' });
    const rows = res.ok ? await res.json() : [];
    setOffers(isLocked ? rows.filter(o => o.section === sectionParam) : rows);
    setLoading(false);
  }, [isLocked, sectionParam]);

  useEffect(() => { load(); }, [load]);

  const respond = async (offer, action, reason) => {
    if (busy) return;
    setBusy(offer.id);
    try {
      const res = await fetch(`${API_URL}/api/lead-offers/${offer.id}/respond`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, decline_reason: reason }),
      });
      if (res.status === 402) { const d = await res.json(); toast.error(formatApiError(d) || 'Top up your Lead Wallet to accept this lead'); navigate('/lead-wallet'); return; }
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      toast.success(action === 'accept' ? 'Lead accepted — it\'s now in your Leads' : 'Lead returned to Ox Resin');
      setDeclineId(null);
      setDeclineReason('');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div
        className="text-white"
        style={isLocked
          ? { background: `linear-gradient(135deg, #0B0B0E 0%, ${SECTION_COLOR[sectionParam]} 70%)` }
          : { background: 'linear-gradient(135deg, #166534, #14532d)' }}
      >
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-4">
          <button onClick={() => navigate(-1)} data-testid="offers-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl md:text-2xl font-bold flex items-center gap-2 truncate">
              <Inbox className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
              {isLocked ? `${SECTION_LABEL[sectionParam]} · Lead Offers` : 'Lead Offers'}
            </h1>
            <p className="hidden sm:block text-white/70 text-sm">Customer details are hidden until you accept · Decline returns the lead to Ox Resin staff.</p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Home"><Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span></Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-3">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : offers.length === 0 ? (
          <Card className="p-10 text-center text-gray-500">No lead offers right now.</Card>
        ) : (
          offers.map(o => (
            <Card key={o.id} data-testid={`offer-${o.id}`} className="p-4">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full" style={{ background: SECTION_COLOR[o.section] }}>{SECTION_LABEL[o.section]}</span>
                    {o.masked ? (
                      <h3 className="font-bold text-gray-900 flex items-center gap-1.5" data-testid={`offer-masked-title-${o.id}`}>
                        <Lock className="w-3.5 h-3.5 text-gray-400" /> Lead in {o.area_hint || o.customer_postcode || '—'}
                      </h3>
                    ) : (
                      <h3 className="font-bold text-gray-900">{o.customer_name || 'Customer'}</h3>
                    )}
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full
                      ${o.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                        o.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' :
                        o.status === 'declined' ? 'bg-rose-100 text-rose-800' :
                        'bg-gray-100 text-gray-600'}`}>
                      {o.status === 'pending' ? <><Clock className="w-3 h-3 inline mr-1" /> {o.status}</> : o.status}
                    </span>
                  </div>

                  {o.masked ? (
                    <>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-gray-700">
                        <div className="flex items-center gap-1.5 bg-gray-50 rounded px-2 py-1.5">
                          <MapPin className="w-3.5 h-3.5 text-gray-400" />
                          <span><span className="text-gray-500">Area:</span> <strong>{o.area_hint || '—'}</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-gray-50 rounded px-2 py-1.5">
                          <Ruler className="w-3.5 h-3.5 text-gray-400" />
                          <span><span className="text-gray-500">Size:</span> <strong>{o.project_size || 'TBC'}</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-gray-50 rounded px-2 py-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span><span className="text-gray-500">Timing:</span> <strong>{o.project_timeline || 'TBC'}</strong></span>
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-2 italic" data-testid={`offer-pii-notice-${o.id}`}>
                        <Lock className="w-3 h-3 inline mr-1" /> Customer name, full postcode and contact details are revealed once you accept.
                      </p>
                      <div className="text-xs text-gray-500 mt-1">Offered {new Date(o.offered_at).toLocaleString('en-GB')} · By {o.offered_by_name}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {o.customer_postcode || '—'} · Offered {new Date(o.offered_at).toLocaleString('en-GB')}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Lead ref: <code>{o.lead_reference}</code> · By {o.offered_by_name}</div>
                    </>
                  )}
                </div>
                {o.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      data-testid={`offer-accept-${o.id}`}
                      onClick={() => respond(o, 'accept')}
                      disabled={busy === o.id}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Accept
                    </Button>
                    <Button
                      data-testid={`offer-decline-${o.id}`}
                      onClick={() => setDeclineId(o.id)}
                      disabled={busy === o.id}
                      size="sm"
                      variant="outline"
                      className="text-rose-600 hover:bg-rose-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Decline
                    </Button>
                  </div>
                )}
                {o.status === 'declined' && o.decline_reason && (
                  <div className="text-xs text-rose-700 italic">&quot;{o.decline_reason}&quot;</div>
                )}
              </div>

              {declineId === o.id && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <Textarea
                    data-testid={`offer-decline-reason-${o.id}`}
                    rows={2}
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    placeholder="Reason for declining (optional — helps Ox Resin route better)"
                  />
                  <div className="flex gap-2 mt-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => { setDeclineId(null); setDeclineReason(''); }}>Cancel</Button>
                    <Button
                      data-testid={`offer-decline-confirm-${o.id}`}
                      size="sm"
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      onClick={() => respond(o, 'decline', declineReason)}
                    >
                      Confirm Decline
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
